/**
 * Top level reducer for the "app" project
 * FIXME: use reselect library to memoize/compose selectors
 *
 * Copyright (c) 2018 Propellant Software Corp
 * Author: Kelvin Nguyen
 */
import { connectRouter } from 'connected-react-router'
import { persistReducer } from 'redux-persist'
import expireReducer from 'redux-persist-expire'
import storage from 'redux-persist/lib/storage'
import pickBy from 'lodash/pickBy'
import { combineReducers } from 'client/shared/js/utils/reducer'
import { bindSelectors } from 'client/shared/js/utils/selector'
import boards, { selectors as boardsSelectors } from './services/boards/reducer'
import lists, { selectors as listsSelectors } from './services/lists/reducer'
import listPlaces, { selectors as listPlacesSelectors } from './services/listPlaces/reducer'
import places, { selectors as placesSelectors } from './services/places/reducer'
import userName from './scenes/Username/reducer'
import sitemap from './scenes/Sitemap/reducer'
import explore, { selectors as exploreSelectors } from './services/explore/reducer'
import guest from './services/guest/reducer'
import user, { selectors as userSelectors } from './services/user/reducer'
import users, { selectors as usersSelectors } from './services/users/reducer'
import auth from 'client/shared/js/services/auth/reducer'
import announcements from 'client/shared/js/services/announcements/reducer'
import globalMessage from 'client/shared/js/services/globalMessage/reducer'
import status, { selectors as statusSelectors } from 'client/shared/js/services/status/reducer'
import featureFlags from 'client/shared/js/services/featureFlags/reducer'

// ------------------------------------
// Reducer
// ------------------------------------

// Expire guest board data in local storage if users didn't update it for 3 days
const guestBoardExpireSeconds = 259200

// Persist guest board data so users won't lose progress on browser refresh
const getPersistConfig = (key) => ({
  key,
  storage: storage,
  whitelist: ['guest'],
  transforms: [
    expireReducer('guest', {
      expireSeconds: guestBoardExpireSeconds,
      autoExpire: true
    })
  ]
})

const rootReducer = (history) => persistReducer(getPersistConfig('root'), combineReducers({
  router: connectRouter(history),
  auth,
  announcements,
  boards: persistReducer(getPersistConfig('boards'), boards),
  lists: persistReducer(getPersistConfig('lists'), lists),
  listPlaces,
  places,
  user,
  users,
  globalMessage,
  status,
  featureFlags,
  sitemap,
  explore,
  browse: combineReducers({
    user: userName
  }),
  guest
}))

export default rootReducer

// ------------------------------------
// Selectors
// ------------------------------------

const selectAuth = (state) => {
  return state.auth
}

const selectBoards = (state) => {
  return state.boards
}

/**
 * Select all lists of a board, which defaults to the current board
 * @param {Object} state The redux store state
 * @param {String} boardId The board id, defaults to current board id
 */
const selectLists = (state, boardId = null) => {
  const bid = boardId ?? selectors.selectCurrentBoard(state)?.bid

  return state.lists[bid] ?? {}
}

// Select "lists" slice-of-state
const _selectLists = (state) => {
  return state.lists
}

/**
 * Select all list places
 * @param {Object} state The redux store state
 */
const selectListPlaces = (state) => {
  return state.listPlaces
}

/**
 * Select all list places within the specified list
 * @param {Object} state The redux store state
 * @param {String} listId The list id
 */
const selectListPlacesWithinList = (state, listId = null) => {
  let listPlaces = {}

  if (listId) {
    listPlaces = selectListPlaces(state)
    listPlaces = pickBy(listPlaces, ({ lid }) => lid === listId)
  }

  return listPlaces
}

// Currently places are equivalent to placeDetails. This will change once we start
// adding more custom data
const selectPlaceDetails = (state) => {
  return state.places
}

const selectHotels = (state, boardId = null) => {
  const bid = boardId ?? selectors.selectCurrentBoard(state)?.bid

  return state.boards[bid]?.hotels ?? {}
}

const selectCurrentUser = (state) => {
  return state.user
}

const selectUsers = (state) => {
  return state.users
}

const selectAnnouncements = (state) => {
  return state.announcements
}

const selectGlobalMessage = (state) => {
  return state.globalMessage
}

const selectStatus = (state) => {
  return state.status
}

const selectFeatureFlags = (state) => {
  return state.featureFlags
}

const selectSitemap = (state) => {
  return state.sitemap
}

const selectExplore = (state) => {
  return state.explore
}

const selectBrowse = (state) => {
  return state.browse
}

export const selectors = {
  selectAuth,
  selectBoards,
  selectLists,
  selectListPlaces,
  selectListPlacesWithinList,
  selectPlaceDetails,
  selectHotels,
  selectCurrentUser,
  selectUsers,
  selectAnnouncements,
  selectGlobalMessage,
  selectStatus,
  selectFeatureFlags,
  selectSitemap,
  selectExplore,
  selectBrowse,
  ...bindSelectors(selectBoards, boardsSelectors),
  ...bindSelectors(_selectLists, listsSelectors),
  ...bindSelectors(selectListPlaces, listPlacesSelectors),
  ...bindSelectors(selectPlaceDetails, placesSelectors),
  ...bindSelectors(selectCurrentUser, userSelectors),
  ...bindSelectors(selectUsers, usersSelectors),
  ...bindSelectors(selectStatus, statusSelectors),
  ...bindSelectors(selectExplore, exploreSelectors)
}
